import {
  BASE_CURRENCY,
  FALLBACK_RATES_FROM_INR,
  isSupportedCurrency,
  normalizeCurrency,
} from "./currencyConfig";

/**
 * Convert an amount in a source currency into INR.
 * rates are INR→foreign (Frankfurter style): price_inr = amount / rates[source].
 */
export function convertToInr(amount, currency, rates = FALLBACK_RATES_FROM_INR) {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  const cur = normalizeCurrency(currency, BASE_CURRENCY);
  const value = Number(amount);
  if (cur === "INR") return value;
  const rate = Number(rates?.[cur]);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return value / rate;
}

/** Convert INR base price into a visitor currency. */
export function convertFromInr(amountInr, currency, rates = FALLBACK_RATES_FROM_INR) {
  if (amountInr == null || !Number.isFinite(Number(amountInr))) return null;
  const cur = normalizeCurrency(currency, BASE_CURRENCY);
  if (cur === "INR") return Number(amountInr);
  const rate = Number(rates?.[cur] ?? FALLBACK_RATES_FROM_INR[cur]);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return Number(amountInr) * rate;
}

/** Locale-aware currency formatting (no hardcoded symbols). */
export function formatCurrency(amount, currency = "USD") {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  const cur = isSupportedCurrency(currency) ? String(currency).toUpperCase() : "USD";
  const value = Number(amount);
  try {
    const options = {
      style: "currency",
      currency: cur,
      maximumFractionDigits: cur === "INR" ? 0 : 2,
      minimumFractionDigits: cur === "INR" ? 0 : 2,
    };
    return new Intl.NumberFormat(undefined, options).format(value);
  } catch {
    return `${cur} ${value.toFixed(cur === "INR" ? 0 : 2)}`;
  }
}

/**
 * Format an INR base amount in the visitor currency.
 * Falls back to INR formatting if conversion fails.
 */
export function formatFromInr(amountInr, currency, rates) {
  if (amountInr == null || !Number.isFinite(Number(amountInr))) return null;
  const cur = normalizeCurrency(currency, "USD");
  const converted = convertFromInr(amountInr, cur, rates);
  if (converted == null) {
    return formatCurrency(amountInr, "INR");
  }
  return formatCurrency(converted, cur);
}
